"use client";

import { PhoneNumberInput } from "@/components/ui/shared/PhoneNumberInput";
import { normalizeExistingPhone } from "@/lib/phone/phone-utils";
import { useEffect, useRef } from "react";

interface PhoneFieldValue {
  phone: string;
  countryDialCodeId: number | null;
}

interface FormPhoneFieldProps {
  value: PhoneFieldValue | string | null;
  onChange?: (value: PhoneFieldValue) => void;
  disabled?: boolean;
  required?: boolean;
  isInvalid?: boolean;
  placeholder?: string;
}

/**
 * Wraps PhoneNumberInput for use in the form field renderer.
 * Handles both the new object format { phone, countryDialCodeId }
 * and legacy plain string values for backward compatibility.
 */
export const FormPhoneField = ({
  value,
  onChange,
  disabled = false,
  isInvalid = false,
  placeholder,
}: FormPhoneFieldProps) => {
  // Derived values
  const phoneValue = typeof value === "object" && value !== null ? value.phone : value || "";
  const countryDialCodeId = typeof value === "object" && value !== null ? value.countryDialCodeId : null;

  // Once the field holds real national digits, it's "theirs" — a later
  // autofill echo (see the effect below) must not silently rewrite it out from
  // under them. A raw "+CC..." value is the not-yet-normalized autofill
  // payload itself, not user-entered digits, so it doesn't count here.
  const hasUserEnteredDigitsRef = useRef(false);

  // Handlers
  const handlePhoneChange = (phone: string) => {
    if (phone && !phone.trim().startsWith("+")) hasUserEnteredDigitsRef.current = true;
    onChange?.({
      phone,
      countryDialCodeId: typeof value === "object" && value !== null ? value.countryDialCodeId : null,
    });
  };

  const handleCountryChange = (id: number | null) => {
    onChange?.({
      phone: typeof value === "object" && value !== null ? value.phone : value || "",
      countryDialCodeId: id,
    });
  };

  // Effects
  // `onChange` is an unstable inline arrow from the form renderer, so we read it
  // through a ref and keep it out of the effect deps — otherwise the effect below
  // would re-run on every parent render (every keystroke anywhere on the form).
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Browser autofill fills the hidden mirror input with the full international
  // number (e.g. "+447700900123"), which lands here as the object's `phone`.
  // PhoneNumberInput can't peel the "+44" off on its own — its country defaults
  // to US on mount, so the dial code gets jammed into the national digits. Since
  // this component owns the merged value, normalize the full number into
  // { national digits, country } in one update so the picker reflects the +CC.
  useEffect(() => {
    if (typeof value !== "object" || value === null) return;
    if (!value.phone || !value.phone.trim().startsWith("+")) return;
    // A late autofill echo racing an active edit — don't overwrite digits the
    // user has already started typing themselves.
    if (hasUserEnteredDigitsRef.current) return;
    const { digits, countryDialCodeId: resolvedCountryDialCodeId } = normalizeExistingPhone(value.phone);
    // Keep the already-selected country if the number carried no recognizable
    // dial code, rather than blanking the picker the user/prefill already set.
    onChangeRef.current?.({ phone: digits, countryDialCodeId: resolvedCountryDialCodeId ?? value.countryDialCodeId });
  }, [value]);

  // Render
  return (
    <PhoneNumberInput
      value={phoneValue}
      onChange={handlePhoneChange}
      countryDialCodeId={countryDialCodeId}
      onCountryDialCodeIdChange={handleCountryChange}
      placeholder={placeholder}
      disabled={disabled}
      error={isInvalid}
    />
  );
};
